import {
  beginAppRequest,
  finishAppRequest,
  observeUpdateProblem,
} from "./app-compatibility";
import axios from "axios";
import { normalizeApiError } from "./normalize-api-error";

import { env } from "@/app/config/env";
import type { RequestScope } from "@/types/request-scope";

// These fields are request metadata, never HTTP headers or persisted credentials.
declare module "axios" {
  interface AxiosRequestConfig {
    requestScope?: RequestScope;
    authGeneration?: number;
    anonymous?: boolean;
  }
}

export interface AuthCredentials {
  accessToken: string | null;
  scope: RequestScope | null;
  generation: number;
}
export type AuthCredentialsProvider = () => AuthCredentials;
export type UnauthorizedHandler = (generation: number) => void;
let getAuthCredentials: AuthCredentialsProvider | null = null;
let handleUnauthorized: UnauthorizedHandler | null = null;

/** Single transport; the app supplies credentials and lifecycle callbacks through dependency inversion. */
export const apiClient = axios.create({
  baseURL: env.VITE_API_BASE_URL,
  timeout: 20_000,
  headers: { Accept: "application/json" },
});

/** Connects session handling without importing feature state into services. Returns a teardown for HMR/tests. */
export function configureApiAuth(
  provider: AuthCredentialsProvider,
  onUnauthorized: UnauthorizedHandler,
) {
  getAuthCredentials = provider;
  handleUnauthorized = onUnauthorized;
  return () => {
    if (getAuthCredentials === provider) {
      getAuthCredentials = null;
      handleUnauthorized = null;
    }
  };
}

apiClient.interceptors.request.use((config) => {
  const credentials = getAuthCredentials?.();
  const scope = config.requestScope;
  if (
    scope &&
    (!credentials?.scope ||
      scope.generation !== credentials.generation ||
      scope.personId !== credentials.scope.personId ||
      scope.tenantId !== credentials.scope.tenantId)
  ) {
    throw new axios.CanceledError("The request session is no longer active.");
  }
  if (
    config.authGeneration !== undefined &&
    config.authGeneration !== credentials?.generation
  ) {
    throw new axios.CanceledError(
      "The request session changed before dispatch.",
    );
  }
  if (credentials) config.authGeneration = credentials.generation;
  // Login is explicitly anonymous. Bound reads cannot silently acquire another tenant's headers.
  if (!config.anonymous && credentials?.accessToken) {
    config.headers.Authorization = `Bearer ${credentials.accessToken}`;
    if (credentials.scope?.tenantId)
      config.headers["X-Tenant-Id"] = credentials.scope.tenantId;
  }
  return beginAppRequest(config);
});

/** Rejects late responses before consumers can cache data or perform navigation side effects. */
function isObsolete(generation: number | undefined): boolean {
  return (
    generation !== undefined && generation !== getAuthCredentials?.().generation
  );
}

apiClient.interceptors.response.use(
  (response) => {
    finishAppRequest(response.config);
    if (isObsolete(response.config.authGeneration))
      throw new axios.CanceledError(
        "The response belongs to an earlier session.",
      );
    return response;
  },
  (error: unknown) => {
    if (axios.isAxiosError(error)) {
      finishAppRequest(error.config);
      observeUpdateProblem(error.response?.data);
      const generation = error.config?.authGeneration;
      if (isObsolete(generation))
        return Promise.reject(
          new axios.CanceledError(
            "The response belongs to an earlier session.",
          ),
        );
      // Invalid login credentials never invalidate an unrelated authenticated request context.
      if (
        error.response?.status === 401 &&
        generation !== undefined &&
        !error.config?.anonymous
      ) {
        handleUnauthorized?.(generation);
      }
    }
    return Promise.reject(normalizeApiError(error));
  },
);
